import { Call, Device } from "@twilio/voice-sdk";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { type stateProps } from "../@types/types";
import { useIsOnline } from "../hooks/useIsOnline";
import { setMakeCallData } from "../store/features/dialer/activeCollapseSlice";
import {
  setCallStatus,
  setOngoingCallTime,
  setSdkClientStatus,
} from "../store/features/dialer/callStatusSlice";
import {
  setAcceptIncomingCallStatus,
  setActiveCallData,
  setCallInfoData,
  setCallState,
  setHangupCallStatus,
  setIsIncoming,
  setIsOnCall,
  setProvider,
} from "../store/features/dialer/deviceSlice";
import { type RootState } from "../store/store";

type IncomingCallProps = {
  callInfo: any;
  accessToken: string;
  setCallInfo: any;
  setAccessTokenError: any;
};

export default function IncomingCall({
  callInfo,
  accessToken,
  setCallInfo,
  setAccessTokenError,
}: IncomingCallProps) {
  const [device, setDevice] = useState<Device | null>(null);
  const [deviceState, setDeviceState] = useState<Device.State | null>(null);
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const { isInternetAvailable } = useIsOnline();
  const dispatch = useDispatch();
  const {
    hangupCallStatus,
    acceptIncomingCallStatus,
    muteUnmuteCallStatus,
    holdUnholdCallStatus,
    isIncoming,
    isOnCall,
    callState,
  } = useSelector((state: RootState) => state?.device);
  const { profile } = useSelector((state: stateProps) => state.global);
  useEffect(() => {
    if (activeCall) {
      const activeCallData = {
        from: activeCall?.parameters?.From,
      };
      dispatch(setActiveCallData(activeCallData));
    }
  }, [activeCall]);

  useEffect(() => {
    if (!device && accessToken) {
      const device = new Device(accessToken);

      device.on("error", (twilioError) => {
        console.log("An error has occurred: ", twilioError);
        setAccessTokenError(true);
      });

      setDevice(device);
      setDeviceState(device.state);
    }
  }, []);

  useEffect(() => {
    if (device) {
      deviceState == "unregistered" && registerDevice();
    }
  }, [device, deviceState]);

  useEffect(() => {
    dispatch(setCallInfoData(callInfo));
    if (device) {
      if (callInfo?.number) {
        if (
          callInfo?.callDirection === "outbound" &&
          callInfo?.sdk === "twilio"
        ) {
          !activeCall && createOutgoingCall();
        }
      }
    }
  }, [callInfo, device]);

  useEffect(() => {
    const handleUnload = (_e: any) => {
      activeCall && hangupCall();
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  useEffect(() => {
    if (device) {
      device.on("registered", () => {
        console.log("The device is ready to receive incoming calls.");
        dispatch(setSdkClientStatus(true));
        setDeviceState(device.state);
      });

      device.on("incoming", (call) => {
        setActiveCall(call);
        dispatch(setCallState(call.status()));
        setDeviceState(device.state);
        setCallStatus(true);
        const activeCallData = {
          from: call?.parameters?.From,
        };
        dispatch(setActiveCallData(activeCallData));
        dispatch(setIsIncoming(true));
        dispatch(setProvider("twilio"));
        setCallInfo({
          ...callInfo,
          ...activeCallData,
          callDirection: "inbound",
        });
      });

      device.on("unregistered", () => {
        dispatch(setSdkClientStatus(false));
        console.log("The device no more receive incoming calls.");
      });

      if (activeCall) {
        activeCall.on("disconnect", () => {
          console.log("The call has been disconnected.");
          hangupCall();
        });

        activeCall.on("accept", () => {
          console.log(
            `The incoming call was accepted or the outgoing call's media session has finished setting up.`
          );
        });

        activeCall.on("cancel", () => {
          hangupCall();
          console.log("The call has been canceled.");
        });

        activeCall.on("error", (error: any) => {
          console.log("An error has occurred: ", error);
        });
      }
    }
  }, [activeCall, callState, device, deviceState, isIncoming]);
  const createOutgoingCall = async () => {
    setCallStatus(true);
    dispatch(setProvider("twilio"));
    try {
      let activeCall = await device?.connect({
        params: {
          To: `${callInfo?.dialcode}${callInfo?.number}`,
          // call_id: callInfo?.callID,
          From: callInfo?.my_phone_number,
          userId: profile?.id,
        },
      });

      setActiveCall(activeCall!);
      dispatch(setCallState(activeCall?.status()));
      setDeviceState(device!.state);
      dispatch(setIsOnCall(true));

      // For outgoing calls, the "accept" event is emitted when the Call's media session has finished setting up.
      activeCall?.on("accept", () => {
        console.log("Outgoing Accepted");
      });
    } catch (error) {
      console.log(error);
    }
  };

  const registerDevice = async () => {
    console.log("Registering the device to receive calls.");
    if (deviceState == "unregistered") {
      await device?.register();
    }
  };

  const acceptIncomingCall = () => {
    activeCall && activeCall.accept();
    console.log("Accepted incoming call");
    dispatch(setCallState("accepted"));
    dispatch(setIsOnCall(true));
  };

  const hangupCall = () => {
    dispatch(setMakeCallData(null));
    activeCall?.disconnect();
    setActiveCall(null);
    dispatch(setCallStatus(false));
    device?.disconnectAll();
    !isOnCall && activeCall && activeCall.reject();
    dispatch(setCallState(null));
    dispatch(setIsIncoming(false));
    dispatch(setIsOnCall(false));
    dispatch(setCallInfoData(null));
    dispatch(setAcceptIncomingCallStatus(false));
    dispatch(setHangupCallStatus(false));
    setCallInfo(null);
    console.log("The call has been hungup.");
    dispatch(setProvider(""));
    dispatch(setOngoingCallTime("reset"));
  };

  const muteUnmuteCall = (isMute: boolean) => {
    if (activeCall) {
      isMute ? activeCall.mute(true) : activeCall.mute(false);
    }
  };

  const holdUnholdCall = (callStatus: boolean) => {
    if (activeCall) {
      console.log(callStatus);
    }
  };

  // const destroyDevice = () => {
  //   console.log('Destroying the device.');
  //   device?.destroy();
  // };

  // const unRegisterDevice = () => {
  //   console.log('Unregistering the device to receive calls.');
  //   deviceState == 'registered' && device?.unregister();
  //   device?.on('unregistered', () =>
  //     console.log('The device no more receive incoming calls.'),
  //   );
  // };

  useEffect(() => {
    hangupCallStatus && hangupCall();
  }, [hangupCallStatus]);

  useEffect(() => {
    muteUnmuteCall(muteUnmuteCallStatus);
  }, [muteUnmuteCallStatus, activeCall]);

  useEffect(() => {
    holdUnholdCall(holdUnholdCallStatus);
  }, [holdUnholdCallStatus, activeCall]);

  useEffect(() => {
    acceptIncomingCallStatus && acceptIncomingCall();
  }, [acceptIncomingCallStatus]);

  useEffect(() => {
    if (!isInternetAvailable) {
      hangupCall();
    }
  }, [isInternetAvailable]);

  return <></>;
}
